import { useContext, useMemo, useState } from 'react';
import { SessionContext } from '../contexts/session/SessionContext';
import useSWR, { keyInterface } from 'swr';
import { fetcherFn, ConfigInterface } from 'swr/dist/types';
import { useMemoOne, useCallbackOne } from 'use-memo-one';
import api from '../api/api';
import { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';

import useSnackbar from './useSnackbar';
import { FlashOnRounded } from '@material-ui/icons';

interface IRequestOptions {
  endpoint: string;
  body?: any;
  onSuccess?: (response: AxiosResponse) => void;
  onError?: (e: AxiosError) => void;
  options?: AxiosRequestConfig;
}

const useApi = <S>(key: keyInterface, options?: ConfigInterface<any, any, fetcherFn<any>>) => {
  const { token, resetSessionTimer, updateToken } = useContext(SessionContext);

  const [isFetching, setIsFetching] = useState(false);

  const pushSnack = useSnackbar();

  const keyForSwr = useMemoOne(() => {
    if (key) {
      return Array.isArray(key) ? [...key, token] : [key, token];
    }
    return null;
  }, [key, token]);

  const handleError = useCallbackOne(
    (error: AxiosError) => {
      pushSnack({ message: error.response?.data?.message?.toString(), severity: 'error' });

      if (error.response?.status === 401) {
        updateToken(undefined);
      }
    },
    [resetSessionTimer],
  );

  const { data, error, isValidating, mutate } = useSWR(keyForSwr, api.getForSwr, {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateOnMount: true,
    onError: handleError,
    onSuccess: resetSessionTimer,
    ...options,
  });

  const get = useCallbackOne(
    async ({ endpoint, onSuccess, onError, options }: IRequestOptions) => {
      try {
        setIsFetching(true);
        const response = await api.get({ endpoint, options, token });
        if (onSuccess) {
          onSuccess(response);
        }
        return response;
      } catch (e) {
        if (onError) {
          onError(e);
        } else {
          handleError(e);
        }
      } finally {
        setIsFetching(false);
        resetSessionTimer();
      }
    },
    [handleError, resetSessionTimer, token],
  );
  const remove = useCallbackOne(
    async ({ endpoint, onSuccess, onError, options }: IRequestOptions) => {
      try {
        setIsFetching(true);
        const response = await api.remove({ endpoint, options, token });
        if (onSuccess) {
          onSuccess(response);
        }
        return response;
      } catch (e) {
        if (onError) {
          onError(e);
        } else {
          handleError(e);
        }
      } finally {
        setIsFetching(false);
        resetSessionTimer();
      }
    },
    [handleError, resetSessionTimer, token],
  );
  const post = useCallbackOne(
    async ({ endpoint, body, onSuccess, onError, options }: IRequestOptions) => {
      try {
        setIsFetching(true);
        const response = await api.post({ endpoint, body, options, token });
        if (onSuccess) {
          onSuccess(response);
        }
        return response;
      } catch (e) {
        if (onError) {
          onError(e);
        } else {
          handleError(e);
        }
      } finally {
        setIsFetching(false);
        resetSessionTimer();
      }
    },
    [handleError, resetSessionTimer, token],
  );

  const patch = useCallbackOne(
    async ({ endpoint, body, onSuccess, onError, options }: IRequestOptions) => {
      try {
        setIsFetching(true);
        const response = await api.patch({ endpoint, body, options, token });
        if (onSuccess) {
          onSuccess(response);
        }
        return response;
      } catch (e) {
        if (onError) {
          onError(e);
        } else {
          handleError(e);
        }
      } finally {
        setIsFetching(false);
        resetSessionTimer();
      }
    },
    [handleError, resetSessionTimer, token],
  );

  const isDataValid = useMemoOne(() => data?.data && !error && !isValidating, [data, error, isValidating]);

  return {
    data: data?.data as S | undefined,
    get,
    post,
    patch,
    remove,
    isDataValid,
    isFetching,
    keyForSwr,
    mutate,
  };
};

export default useApi;
